import { IKlarnaValues, IKlarnaData, IKlarnaConfig } from '../components/Klarna/types'
import { raygun } from '@ynap/clientside-monitoring'
import { GuestIdentityReissuedError } from '@ynap/fetch'

export const injectKlarnaScript = (config: IKlarnaConfig, locale: string, id: string, isExpress = false): void => {
    const { onSiteMessaging = {}, express = {}, environment } = config || {}
    const { cdnUrl } = isExpress ? express : onSiteMessaging || {}
    const scriptId = `klarna-${isExpress ? 'express' : 'onSiteMessaging'}`

    const shouldInject = locale && typeof document !== 'undefined' && !document.getElementById(scriptId)
    if (!shouldInject) return

    const script = document.createElement('script')
    script.id = scriptId
    script.src = cdnUrl || ''
    script.async = true
    script.setAttribute(isExpress ? 'data-id' : 'data-client-id', id)
    script.setAttribute('data-environment', environment || 'playground')

    const firstScriptTag = document.getElementsByTagName('script')[0]
    const head = document.getElementsByTagName('head')[0]
    firstScriptTag ? firstScriptTag.parentNode?.insertBefore(script, firstScriptTag) : head?.appendChild(script)
}

export const initKlarnaExpressFlow = async (values: IKlarnaValues) => {
    const { setLoadingState, setShippingAddress, setPaymentMethod, setKlarnaExpress, data, locale, history } = values

    try {
        setLoadingState?.(true)

        // Add transient address from Klarna
        const klarnaAddress = mapKlarnaAddress(data)
        const { addressId } = await setShippingAddress(klarnaAddress)
        await setPaymentMethod({ paymentMethodId: 'KLARNA', billingAddress: { addressId, country: locale.country }, paymentFromCart: 'true' })

        setKlarnaExpress({ values: null })
        history.push(`/${locale.id}/checkout/payment-options`)
    } catch (error) {
        let errorCode
        if (error === Object(error)) {
            const { errors, code, errorKey } = error
            errorCode = errors?.[0]?.errorKey || code || errorKey
        }

        setKlarnaExpress({ error: String(errorCode) })
        setLoadingState?.(false)
        handleErrors(error, history, locale)

        raygun.helpers.agent('send', {
            error: error,
            tags: ['KLARNA_EXPRESS'],
        })
    }
}

export const handleErrors = (error: Error | GuestIdentityReissuedError, history, locale: Locale) => {
    if (error instanceof GuestIdentityReissuedError) {
        return history.push(`/${locale.id}/checkout/login`)
    }
    return history.push(`/${locale.id}/checkout/payment-options`)
}

export const mapKlarnaAddress = (data: IKlarnaData) => {
    const { first_name: firstName, last_name: lastName, address, phone: phone1 } = data

    const { street_address, street_address2, postal_code: zipCode, region: state, city, country } = address

    return {
        addressType: 'ShippingAndBilling',
        firstName,
        lastName,
        addressLine: [street_address, ...(street_address2 ? [street_address2] : [])],
        city,
        state,
        zipCode,
        country,
        phone1,
        isTransient: true,
        externalAddressValidation: 'true',
    }
}

export type IAddressType = ReturnType<typeof mapKlarnaAddress>
